import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  Divider,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  MenuItem,
  Alert
} from '@mui/material';
import {
  Save as SaveIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  ColorLens as ColorLensIcon,
  FormatSize as FormatSizeIcon,
  TextFormat as TextFormatIcon
} from '@mui/icons-material';
import { DisplaySettings as DisplaySettingsType } from '../../../types/storeSettings';
import { useStoreSettings } from '../../../contexts/StoreSettingsContext';
import { Promotion } from '../ProductPromotion';
import ImageUpload from '../../common/ImageUpload';

interface PromotionSettingsProps {
  settings: DisplaySettingsType;
  onSave: (loading: boolean) => void;
  onError: (error: string | null) => void;
  onSuccess: (success: boolean) => void;
}

const PromotionSettings: React.FC<PromotionSettingsProps> = ({
  settings,
  onSave,
  onError,
  onSuccess
}) => {
  const { updateSection } = useStoreSettings();
  const [formData, setFormData] = useState<DisplaySettingsType>(settings);
  const [editing, setEditing] = useState(false);

  // حالة مربع حوار الإعلان
  const [promotionDialogOpen, setPromotionDialogOpen] = useState(false);
  const [currentPromotion, setCurrentPromotion] = useState<Promotion | null>(null);
  const [isNewPromotion, setIsNewPromotion] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      onSave(true);
      onError(null);

      await updateSection('display', formData);

      onSuccess(true);
      setEditing(false);
    } catch (error) {
      onError('حدث خطأ أثناء حفظ إعدادات الإعلانات');
    } finally {
      onSave(false);
    }
  };

  // وظائف الإعلان
  const handleOpenPromotionDialog = (promotion?: Promotion) => {
    if (promotion) {
      setCurrentPromotion(promotion);
      setIsNewPromotion(false);
    } else {
      setCurrentPromotion({
        id: `promo-${Date.now()}`,
        title: '',
        description: '',
        imageUrl: '',
        backgroundColor: '#f44336',
        textColor: '#ffffff',
        buttonText: 'تسوق الآن',
        buttonLink: '/store/categories',
        position: 'right',
        size: 'medium',
        titleFontSize: '1.5rem',
        titleFontFamily: 'Cairo, sans-serif',
        descriptionFontSize: '1rem',
        descriptionFontFamily: 'Cairo, sans-serif'
      });
      setIsNewPromotion(true);
    }
    setPromotionDialogOpen(true);
  };

  const handleClosePromotionDialog = () => {
    setPromotionDialogOpen(false);
    setCurrentPromotion(null);
  };

  const handleSavePromotion = async () => {
    if (!currentPromotion) return;

    try {
      onSave(true);

      let updatedPromotions: Promotion[];

      if (isNewPromotion) {
        updatedPromotions = [...formData.promotions, currentPromotion];
      } else {
        updatedPromotions = formData.promotions.map(promotion =>
          promotion.id === currentPromotion.id ? currentPromotion : promotion
        );
      }

      // تحديث البيانات المحلية
      setFormData(prev => ({ ...prev, promotions: updatedPromotions }));

      // حفظ التغييرات في قاعدة البيانات مباشرة
      console.log('حفظ الإعلانات في قاعدة البيانات:', updatedPromotions);
      await updateSection('display', { ...formData, promotions: updatedPromotions });

      // إغلاق مربع الحوار
      handleClosePromotionDialog();

      // عرض رسالة نجاح
      onSuccess(true);
    } catch (error) {
      console.error('خطأ في حفظ الإعلان:', error);
      onError('حدث خطأ أثناء حفظ الإعلان');
    } finally {
      onSave(false);
    }
  };

  const handleDeletePromotion = async (promotionId: string) => {
    if (window.confirm('هل أنت متأكد من حذف هذا الإعلان؟')) {
      try {
        onSave(true);

        // تحديث البيانات المحلية
        const updatedPromotions = formData.promotions.filter(promotion => promotion.id !== promotionId);
        setFormData(prev => ({ ...prev, promotions: updatedPromotions }));

        // حفظ التغييرات في قاعدة البيانات مباشرة
        console.log('حفظ الإعلانات بعد الحذف في قاعدة البيانات:', updatedPromotions);
        await updateSection('display', { ...formData, promotions: updatedPromotions });

        // عرض رسالة نجاح
        onSuccess(true);
      } catch (error) {
        console.error('خطأ في حذف الإعلان:', error);
        onError('حدث خطأ أثناء حذف الإعلان');
      } finally {
        onSave(false);
      }
    }
  };

  const handlePromotionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!currentPromotion) return;

    const { name, value } = e.target;
    setCurrentPromotion(prev => prev ? { ...prev, [name]: value } : null);
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" component="h2">
          إعدادات الإعلانات الترويجية
        </Typography>

        {!editing ? (
          <Button
            startIcon={<EditIcon />}
            onClick={() => {
              console.log('Edit button clicked, setting editing to true');
              setEditing(true);
            }}
            variant="outlined"
          >
            تعديل
          </Button>
        ) : (
          <Button
            type="submit"
            startIcon={<SaveIcon />}
            variant="contained"
            color="primary"
          >
            حفظ التغييرات
          </Button>
        )}
      </Box>

      {/* زر إضافي لتفعيل التحرير مباشرة */}
      <Box sx={{ mb: 2 }}>
        <Button
          variant="contained"
          color="secondary"
          fullWidth
          onClick={() => {
            console.log('Force edit button clicked');
            setEditing(true);
          }}
        >
          انقر هنا لتفعيل التحرير مباشرة
        </Button>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1">
          الإعلانات الترويجية ({formData.promotions.length})
        </Typography>

        <Button
          startIcon={<AddIcon />}
          variant="contained"
          color="primary"
          onClick={() => handleOpenPromotionDialog()}
          disabled={!editing}
        >
          إضافة إعلان جديد
        </Button>
      </Box>

      {formData.promotions.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          لا توجد إعلانات ترويجية. قم بإضافة إعلان جديد لعرضه في صفحات المتجر.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {formData.promotions.map((promotion) => (
            <Grid item xs={12} md={6} key={promotion.id}>
              <Card sx={{ height: '100%' }}>
                <Box sx={{
                  height: 140,
                  backgroundColor: promotion.backgroundColor || '#f44336',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: promotion.textColor || '#ffffff',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {promotion.imageUrl ? (
                    <CardMedia
                      component="img"
                      height="140"
                      image={promotion.imageUrl}
                      alt={promotion.title}
                      sx={{ opacity: 0.7 }}
                    />
                  ) : (
                    <Typography variant="h5" component="div" sx={{ p: 2, textAlign: 'center' }}>
                      {promotion.title}
                    </Typography>
                  )}
                </Box>
                <CardContent>
                  <Typography
                    variant="h6"
                    component="div"
                    gutterBottom
                    sx={{
                      fontSize: promotion.titleFontSize || '1.5rem',
                      fontFamily: promotion.titleFontFamily || 'Cairo, sans-serif'
                    }}
                  >
                    {promotion.title}
                    {promotion.titleFontSize && (
                      <Typography variant="caption" component="span" sx={{ ml: 1, color: 'text.secondary' }}>
                        ({promotion.titleFontSize})
                      </Typography>
                    )}
                  </Typography>
                  {promotion.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                      sx={{
                        fontSize: promotion.descriptionFontSize || '1rem',
                        fontFamily: promotion.descriptionFontFamily || 'Cairo, sans-serif'
                      }}
                    >
                      {promotion.description}
                      {promotion.descriptionFontSize && (
                        <Typography variant="caption" component="span" sx={{ ml: 1, color: 'text.secondary' }}>
                          ({promotion.descriptionFontSize})
                        </Typography>
                      )}
                    </Typography>
                  )}
                  <Grid container spacing={1} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        الموضع: {promotion.position === 'right' ? 'يمين' : 'يسار'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        الحجم: {
                          promotion.size === 'small' ? 'صغير' :
                          promotion.size === 'large' ? 'كبير' : 'متوسط'
                        }
                      </Typography>
                    </Grid>
                    {promotion.buttonText && (
                      <Grid item xs={12}>
                        <Typography variant="body2">
                          زر: {promotion.buttonText}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                        <FormatSizeIcon fontSize="small" sx={{ mr: 0.5 }} />
                        خط العنوان: {promotion.titleFontFamily?.split(',')[0] || 'Cairo'} / {promotion.titleFontSize || '1.5rem'}
                      </Typography>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                        <FormatSizeIcon fontSize="small" sx={{ mr: 0.5 }} />
                        خط الوصف: {promotion.descriptionFontFamily?.split(',')[0] || 'Cairo'} / {promotion.descriptionFontSize || '1rem'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenPromotionDialog(promotion)}
                    disabled={!editing}
                  >
                    تعديل
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeletePromotion(promotion.id)}
                    disabled={!editing}
                  >
                    حذف
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* مربع حوار إضافة/تعديل الإعلان */}
      <Dialog
        open={promotionDialogOpen}
        onClose={handleClosePromotionDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {isNewPromotion ? 'إضافة إعلان جديد' : 'تعديل الإعلان'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="العنوان"
                name="title"
                value={currentPromotion?.title || ''}
                onChange={handlePromotionChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  صورة الإعلان (اختياري)
                </Typography>
                <ImageUpload
                  initialImage={currentPromotion?.imageUrl || ''}
                  onImageUploaded={(url) => {
                    if (currentPromotion) {
                      setCurrentPromotion({
                        ...currentPromotion,
                        imageUrl: url
                      });
                    }
                  }}
                  label="صورة الإعلان"
                  folder="promotions"
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="الوصف"
                name="description"
                value={currentPromotion?.description || ''}
                onChange={handlePromotionChange}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="نص الزر"
                name="buttonText"
                value={currentPromotion?.buttonText || ''}
                onChange={handlePromotionChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="رابط الزر"
                name="buttonLink"
                value={currentPromotion?.buttonLink || ''}
                onChange={handlePromotionChange}
                helperText="مثال: /store/categories"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="لون الخلفية"
                name="backgroundColor"
                value={currentPromotion?.backgroundColor || '#f44336'}
                onChange={handlePromotionChange}
                type="color"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ColorLensIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="لون النص"
                name="textColor"
                value={currentPromotion?.textColor || '#ffffff'}
                onChange={handlePromotionChange}
                type="color"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ColorLensIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="الموضع"
                name="position"
                value={currentPromotion?.position || 'right'}
                onChange={handlePromotionChange}
              >
                <MenuItem value="left">يسار</MenuItem>
                <MenuItem value="right">يمين</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="الحجم"
                name="size"
                value={currentPromotion?.size || 'medium'}
                onChange={handlePromotionChange}
              >
                <MenuItem value="small">صغير</MenuItem>
                <MenuItem value="medium">متوسط</MenuItem>
                <MenuItem value="large">كبير</MenuItem>
              </TextField>
            </Grid>

            {/* إعدادات الخط */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }}>
                <FormatSizeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                إعدادات الخط
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid>

            {/* حجم خط العنوان */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="حجم خط العنوان"
                name="titleFontSize"
                value={currentPromotion?.titleFontSize || '1.5rem'}
                onChange={handlePromotionChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FormatSizeIcon />
                    </InputAdornment>
                  ),
                }}
              >
                <MenuItem value="1rem">صغير جداً (1rem)</MenuItem>
                <MenuItem value="1.25rem">صغير (1.25rem)</MenuItem>
                <MenuItem value="1.5rem">متوسط (1.5rem)</MenuItem>
                <MenuItem value="1.75rem">كبير (1.75rem)</MenuItem>
                <MenuItem value="2rem">كبير جداً (2rem)</MenuItem>
                <MenuItem value="2.5rem">ضخم (2.5rem)</MenuItem>
                <MenuItem value="3rem">ضخم جداً (3rem)</MenuItem>
              </TextField>
            </Grid>

            {/* نوع خط العنوان */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="نوع خط العنوان"
                name="titleFontFamily"
                value={currentPromotion?.titleFontFamily || 'Cairo, sans-serif'}
                onChange={handlePromotionChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TextFormatIcon />
                    </InputAdornment>
                  ),
                }}
              >
                <MenuItem value="Cairo, sans-serif">Cairo</MenuItem>
                <MenuItem value="Tajawal, sans-serif">Tajawal</MenuItem>
                <MenuItem value="Amiri, serif">Amiri</MenuItem>
                <MenuItem value="Almarai, sans-serif">Almarai</MenuItem>
                <MenuItem value="Reem Kufi, sans-serif">Reem Kufi</MenuItem>
                <MenuItem value="Scheherazade, serif">Scheherazade</MenuItem>
              </TextField>
            </Grid>

            {/* حجم خط الوصف */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="حجم خط الوصف"
                name="descriptionFontSize"
                value={currentPromotion?.descriptionFontSize || '1rem'}
                onChange={handlePromotionChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FormatSizeIcon />
                    </InputAdornment>
                  ),
                }}
              >
                <MenuItem value="0.75rem">صغير جداً (0.75rem)</MenuItem>
                <MenuItem value="0.875rem">صغير (0.875rem)</MenuItem>
                <MenuItem value="1rem">متوسط (1rem)</MenuItem>
                <MenuItem value="1.125rem">كبير (1.125rem)</MenuItem>
                <MenuItem value="1.25rem">كبير جداً (1.25rem)</MenuItem>
                <MenuItem value="1.5rem">ضخم (1.5rem)</MenuItem>
              </TextField>
            </Grid>

            {/* نوع خط الوصف */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="نوع خط الوصف"
                name="descriptionFontFamily"
                value={currentPromotion?.descriptionFontFamily || 'Cairo, sans-serif'}
                onChange={handlePromotionChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TextFormatIcon />
                    </InputAdornment>
                  ),
                }}
              >
                <MenuItem value="Cairo, sans-serif">Cairo</MenuItem>
                <MenuItem value="Tajawal, sans-serif">Tajawal</MenuItem>
                <MenuItem value="Amiri, serif">Amiri</MenuItem>
                <MenuItem value="Almarai, sans-serif">Almarai</MenuItem>
                <MenuItem value="Reem Kufi, sans-serif">Reem Kufi</MenuItem>
                <MenuItem value="Scheherazade, serif">Scheherazade</MenuItem>
              </TextField>
            </Grid>

            {currentPromotion?.imageUrl && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  معاينة الصورة
                </Typography>
                <Box
                  component="img"
                  src={currentPromotion.imageUrl}
                  alt="Promotion Preview"
                  sx={{
                    width: '100%',
                    maxHeight: '200px',
                    objectFit: 'cover',
                    borderRadius: 1,
                    mt: 1
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x200?text=Error+Loading+Image';
                  }}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePromotionDialog} color="inherit">
            إلغاء
          </Button>
          <Button onClick={handleSavePromotion} color="primary" variant="contained">
            حفظ
          </Button>
        </DialogActions>
      </Dialog>

      {editing && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setEditing(false)}
            sx={{ mr: 2 }}
          >
            إلغاء
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
          >
            حفظ التغييرات
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PromotionSettings;
